import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Star, ArrowRight, Quote } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useT } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Reviews = () => {
  const { lang } = useLanguage();
  const t = useT(lang);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    axios.get(`${API}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  const avg =
    reviews.length > 0
      ? (reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length).toFixed(1)
      : "5.0";

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden py-24 md:py-32">
        <div className="absolute right-[-10%] top-[-6%] cn-bg">評</div>
        <div className="container-wc relative z-10 max-w-4xl fade-up">
          <div className="flex items-center gap-3 mb-4">
            <span className="divider-gold" />
            <span className="eyebrow">{lang === "el" ? "Κριτικές μαθητών" : "Student reviews"}</span>
          </div>
          <h1 className="h-display text-5xl md:text-7xl mb-6">
            {lang === "el" ? "Οι φωνές των μαθητών" : "The voices of the students"}
          </h1>
          <p className="text-dim text-lg leading-relaxed max-w-2xl mb-8">
            {lang === "el"
              ? "Τι λένε όσοι έχουν εκπαιδευτεί με τον Sifu Νίκο Αγαθόπουλο για την εμπειρία τους στα ιδιαίτερα μαθήματα Wing Chun και αυτοάμυνας."
              : "What those who have trained with Sifu Nikos Agathopoulos say about their experience in private Wing Chun and self-defense lessons."}
          </p>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} size={22} fill="#D4AF37" stroke="#D4AF37" />
              ))}
            </div>
            <span className="font-serif text-3xl text-gold">{avg}</span>
            <span className="text-dim text-sm">
              {lang === "el" ? `από ${reviews.length} κριτικές` : `from ${reviews.length} reviews`}
            </span>
          </div>
        </div>
      </section>

      {/* REVIEWS GRID */}
      <section className="py-16 md:py-20">
        <div className="container-wc">
          {reviews.length === 0 ? (
            <p className="text-dim text-center py-20">
              {lang === "el" ? "Δεν υπάρχουν ακόμα κριτικές." : "No reviews yet."}
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {reviews.map((r, i) => (
                <div key={r.id} data-testid={`review-${i}`} className="card relative">
                  <Quote size={40} className="text-primary opacity-30 absolute top-6 right-6" />
                  <div className="flex items-center gap-1 mb-4">
                    {[1, 2, 3, 4, 5].map((n) => {
                      const active = n <= (r.rating || 5);
                      return (
                        <Star
                          key={n}
                          size={16}
                          fill={active ? "#D4AF37" : "none"}
                          stroke={active ? "#D4AF37" : "#666"}
                        />
                      );
                    })}
                  </div>
                  <p className="text-[#E8E8E0] leading-relaxed mb-6 text-base italic">
                    &ldquo;{lang === "el" ? r.text_el : r.text_en}&rdquo;
                  </p>
                  <div className="pt-4 border-t border-goldSoft">
                    <div className="font-serif text-lg text-gold">{r.name}</div>
                    {r.class_type && (
                      <div className="text-dim text-xs uppercase tracking-widest mt-1">
                        {r.class_type}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 border-t border-goldSoft">
        <div className="container-wc text-center max-w-3xl mx-auto">
          <span className="font-cn text-gold text-5xl block mb-6">學</span>
          <h2 className="h-display text-3xl md:text-4xl mb-6">
            {lang === "el" ? "Γίνετε ο επόμενος μαθητής" : "Become the next student"}
          </h2>
          <p className="text-dim mb-8">
            {lang === "el"
              ? "Κλείστε ένα δοκιμαστικό μάθημα και ξεκινήστε το δικό σας ταξίδι στο Wing Chun."
              : "Book a trial lesson and start your own Wing Chun journey."}
          </p>
          <Link to="/contact" data-testid="reviews-cta" className="btn-primary">
            {t.hero.cta_primary} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Reviews;
